import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PanelHeader from "components/panelheader/panelHeader";
import { FormGroup, Input, Button, Card, CardBody, Row, Col } from "reactstrap";
import Select from "react-select";
import { getJugador, saveJugador } from "services/jugadoresService";
import { getEquipos } from "services/equiposService";
import { toastBigError } from "components/utils/toasts";
import Joi from "joi-browser";
import { usePageLoading } from "hooks/usePageLoading";

// La fecha de nacimiento es una fecha "sin hora". Se guarda al mediodía UTC y se
// lee con los getters locales para que ningún huso horario la corra un día: la
// lista la formatea con moment en hora local y quedaría desfasada.
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
};

const fromDateInput = (value) => (value ? value + "T12:00:00.000Z" : null);

const JugadoresForm = (props) => {
  const doneLoading = usePageLoading(props);
  let { id } = useParams();
  let location = useLocation();
  let navigate = useNavigate();
  const validateForm = true;
  const [data, setData] = React.useState({
    _id: id ? id : null,
    apellido: "",
    nombre: "",
    dni: "",
    fechaNacimiento: "",
    email: "",
    telefono: "",
    equipo: "",
    camiseta: "",
    isExcepcion: false,
  });
  const [errors, setErrors] = React.useState([]);
  const [equipos, setEquipos] = React.useState([]);
  const [action, setAction] = React.useState(location.state?.action ? location.state.action : "");

  const readOnly = action === "C";

  const schema = {
    _id: Joi.string(),
    apellido: Joi.string()
      .label("Apellido")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe ingresar un apellido" }))),
    nombre: Joi.string()
      .label("Nombre")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe ingresar un nombre" }))),
    dni: Joi.string()
      .label("DNI")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe ingresar el DNI" }))),
    fechaNacimiento: Joi.string().label("Fecha de Nacimiento").allow("").optional(),
    email: Joi.string()
      .label("Email")
      .email()
      .allow("")
      .optional()
      .error((errors) => errors.map(() => ({ message: "Debe ingresar un email válido" }))),
    telefono: Joi.string().label("Teléfono").allow("").optional(),
    equipo: Joi.string().label("Equipo").allow("").optional(),
    camiseta: Joi.string().label("Camiseta").allow("").optional(),
    isExcepcion: Joi.boolean().label("Excepción"),
  };

  const fetchData = async (id) => {
    const record = await getJugador(id);
    if (record === "NETWORK ERROR") {
      toastBigError("Error", "Error de Conexión");
      return;
    }
    mapToViewModel(record);
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    // Esta pantalla trae dos cosas (equipos y jugador): el loader se libera
    // recién cuando terminaron todas.
    const pendientes = [fetchEquipos()];
    const id = data._id;
    if (id && !id.includes("nuev")) {
      pendientes.push(fetchData(id));
    } else {
      const cacheData = { ...data };
      delete cacheData._id;
      setData(cacheData);
      setAction("A");
    }
    Promise.allSettled(pendientes).then(doneLoading);
  }, []);

  const fetchEquipos = async () => {
    const records = await getEquipos();
    if (Array.isArray(records)) setEquipos(records.map((item) => item.nombre));
  };

  const mapToViewModel = (jugador) => {
    setData({
      _id: jugador._id,
      apellido: jugador.apellido || "",
      nombre: jugador.nombre || "",
      dni: jugador.dni || "",
      fechaNacimiento: toDateInput(jugador.fechaNacimiento),
      email: jugador.email || "",
      telefono: jugador.telefono || "",
      equipo: jugador.equipo || "",
      camiseta: jugador.camiseta || "",
      isExcepcion: jugador.isExcepcion === true,
    });
  };

  // El jugador guarda el NOMBRE del equipo (así lo cruza TeamsManager), no el
  // _id. Si viene de una importación puede tener un equipo que ya no existe en
  // el listado: se agrega a las opciones para no perderlo al grabar.
  const equipoOptions = () => {
    const nombres = data.equipo && !equipos.includes(data.equipo) ? [data.equipo, ...equipos] : equipos;
    return nombres.map((nombre) => ({ value: nombre, label: nombre }));
  };

  const handleChange = (event) => {
    const input = event.currentTarget;
    const inputMode = event.target.inputMode;
    const cacheErrors = { ...errors };
    const cacheData = { ...data };
    if (validateForm) {
      const errorMessage = validateProperty(input, inputMode);
      if (errorMessage) {
        cacheErrors[input.name] = errorMessage;
      } else delete cacheErrors[input.name];
    } else delete cacheErrors[input.name];
    cacheData[input.name] = input.value;
    if (input.type === "checkbox") cacheData[input.name] = input.checked;
    setData(cacheData);
    setErrors(cacheErrors);
  };

  const handleSelectEquipo = (option) => {
    const cacheErrors = { ...errors };
    const cacheData = { ...data };
    cacheData.equipo = option ? option.value : "";
    delete cacheErrors.equipo;
    setData(cacheData);
    setErrors(cacheErrors);
  };

  const validateRules = () => {
    return null;
  };

  const validate = () => {
    let errors = {};
    const options = { abortEarly: false };
    if (validateForm) {
      Object.keys(data).map(function (key, index) {
        if (typeof data[key] === "object" && data[key] && data[key].value && typeof data[key].value === "string") {
          data[key] = data[key].value;
        }
      });
      let { error } = Joi.validate(data, schema, options);
      if (!error) {
        error = validateRules();
        if (!error) {
          Object.keys(data).map(function (key, index) {
            if (typeof data[key] === "object" && data[key] && data[key].value) data[key] = data[key].value;
          });
          return null;
        }
        error.map((error) => {
          toastBigError(error.message, 2500);
          return (errors[error.path] = error.message);
        });
        return errors;
      } else {
        error.details.map((validationError) => {
          return (errors[validationError.path[0]] = validationError.message);
        });
        return errors;
      }
    } else return null;
  };

  const validateProperty = (input, inputMode) => {
    let { name, value } = input;
    const object = { [name]: value };
    const checkSchema = { [name]: schema[name] };
    const { error } = Joi.validate(object, checkSchema);
    return error ? error.details[0].message : null;
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    const checkErrors = validate();
    if (checkErrors) {
      setErrors(checkErrors);
      return;
    }
    doSubmit();
  };

  const doSubmit = async () => {
    const payload = { ...data, fechaNacimiento: fromDateInput(data.fechaNacimiento) };
    try {
      const response = await saveJugador(payload);
      // El backend responde 200 con success:false cuando el DNI ya existe.
      if (response && response.data && response.data.success === false) {
        toastBigError("Error", response.data.message);
        return;
      }
      navigate(-1);
    } catch (ex) {
      const message = ex.response && ex.response.data ? ex.response.data.message : "No se pudo grabar el jugador";
      toastBigError("Error", message);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Row>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["apellido"] ? "has-danger" : "")}>
                      <label>Apellido</label>
                      <Input
                        id="apellido"
                        name="apellido"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.apellido}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["nombre"] ? "has-danger" : "")}>
                      <label>Nombre</label>
                      <Input
                        id="nombre"
                        name="nombre"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.nombre}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["dni"] ? "has-danger" : "")}>
                      <label>DNI</label>
                      <Input
                        id="dni"
                        name="dni"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.dni}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["fechaNacimiento"] ? "has-danger" : "")}>
                      <label>Fecha de Nacimiento</label>
                      <Input
                        id="fechaNacimiento"
                        name="fechaNacimiento"
                        type="date"
                        disabled={readOnly}
                        value={data.fechaNacimiento}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["email"] ? "has-danger" : "")}>
                      <label>Email</label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.email}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["telefono"] ? "has-danger" : "")}>
                      <label>Teléfono</label>
                      <Input
                        id="telefono"
                        name="telefono"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.telefono}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["equipo"] ? "has-danger" : "")}>
                      <label>Equipo</label>
                      <Select
                        className="react-select"
                        classNamePrefix="react-select"
                        placeholder="Seleccione un equipo"
                        name="equipo"
                        isClearable
                        isDisabled={readOnly}
                        value={data.equipo ? { value: data.equipo, label: data.equipo } : null}
                        onChange={handleSelectEquipo}
                        options={equipoOptions()}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className={`has-label ` + (errors["camiseta"] ? "has-danger" : "")}>
                      <label>Camiseta</label>
                      <Input
                        id="camiseta"
                        name="camiseta"
                        autoComplete="off"
                        disabled={readOnly}
                        value={data.camiseta}
                        onChange={(e) => handleChange(e)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <div className="form-check">
                      <label className="form-check-label">
                        <input
                          className="form-check-input"
                          id="isExcepcion"
                          name="isExcepcion"
                          type="checkbox"
                          disabled={readOnly}
                          checked={data.isExcepcion}
                          onChange={(e) => handleChange(e)}
                        />
                        <span className="form-check-sign" />
                        Es excepción en su equipo
                      </label>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <Button className="pull-right btn-cancelar" onClick={goBack}>
                      {readOnly ? "Volver" : "Cancelar"}
                    </Button>
                    {!readOnly && (
                      <Button className="pull-right btn-arhock" onClick={handleSubmit}>
                        Aceptar
                      </Button>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default JugadoresForm;
